import type {
  AgentEvalCaseResultResponse,
  AgentEvalRunResponse,
} from '@erp/rpc';
import { Badge } from 'src/components/ui/badge';

type Outcome = AgentEvalCaseResultResponse['outcome'];
type RunStatus = AgentEvalRunResponse['status'];

const OUTCOME_META: Record<
  Outcome,
  {
    label: string;
    variant: 'success' | 'destructive' | 'warning' | 'secondary';
  }
> = {
  agent_pass: { label: '✓ Pass', variant: 'success' },
  agent_fail: { label: '✕ Fail', variant: 'destructive' },
  safety_violation: { label: '⚠ Safety', variant: 'warning' },
  environment_unavailable: { label: 'Env down', variant: 'secondary' },
  environment_changed: { label: 'Env changed', variant: 'secondary' },
};

export function OutcomeBadge({ outcome }: { outcome: Outcome }) {
  const meta = OUTCOME_META[outcome];
  return <Badge variant={meta.variant}>{meta.label}</Badge>;
}

const STATUS_META: Record<
  RunStatus,
  {
    label: string;
    variant: 'success' | 'destructive' | 'warning' | 'secondary';
  }
> = {
  completed: { label: 'Completed', variant: 'success' },
  running: { label: 'Running', variant: 'warning' },
  queued: { label: 'Queued', variant: 'secondary' },
  failed: { label: 'Failed', variant: 'destructive' },
  cancelled: { label: 'Cancelled', variant: 'secondary' },
};

export function StatusBadge({ status }: { status: RunStatus }) {
  const meta = STATUS_META[status];
  return <Badge variant={meta.variant}>{meta.label}</Badge>;
}

// Rubric as a weighted percentage of criteria met (each criterion equally
// weighted — success criteria are boolean, so there's no per-item score to
// weight by). "—" when there are no criteria.
export function formatRubricPct(met: number, total: number): string {
  if (total === 0) return '—';
  return `${Math.round((met / total) * 100)}%`;
}

export function formatCost(usd: number | null): string {
  if (usd === null) return '—';
  return `$${usd.toFixed(usd < 1 ? 3 : 2)}`;
}

export function formatLatency(ms: number | null): string {
  if (ms === null) return '—';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

export function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
